<template>
  <!-- One lexicon's entry, from typed runs: nothing from the source is rendered as markup. -->
  <article class="lexicon-card">
    <header>
      <span class="title">{{ card.title }}</span>
      <span class="entry">{{ card.entry }}</span>
    </header>
    <p class="head">
      <span class="headword" :class="scriptClass">{{ card.headword }}</span>
      <span v-if="card.translit" class="translit">{{ card.translit }}</span>
      <span v-if="card.pos" class="pos">{{ card.pos }}</span>
      <span v-if="card.gloss" class="gloss">{{ card.gloss }}</span>
    </p>
    <div class="body">
      <p v-for="(p, i) in card.body" :key="i" class="paragraph" :style="{ paddingLeft: `${p.depth * 1.1}rem` }">
        <b v-if="p.label" class="label">{{ p.label }}</b>
        <template v-for="(r, j) in p.runs" :key="j">
          <b v-if="r.kind === 'definition'" class="definition">{{ r.text }}</b>
          <i v-else-if="r.kind === 'emphasis' || r.kind === 'foreign'">{{ r.text }}</i>
          <span v-else-if="r.kind === 'hebrew'" class="font-hebrew">{{ r.text }}</span>
          <span v-else-if="r.kind === 'greek'" class="font-greek">{{ r.text }}</span>
          <span v-else-if="r.kind === 'reference'" class="reference" :title="r.verse ? verseRef(r.verse) : undefined">{{ r.text }}</span>
          <a v-else-if="r.kind === 'cross_reference' && isStrongs(r.entry)" class="cross" @click="$emit('open-strongs', r.entry)">{{ r.text }}</a>
          <span v-else>{{ r.text }}</span>
        </template>
      </p>
    </div>
    <footer class="attribution">{{ card.attribution }}</footer>
  </article>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'
import { LANGUAGES } from '@/components/lemmas/renderer/wordLayer'
import { LexiconCard } from '../api'
import { verseRef } from '../format'

export default defineComponent({
  name: 'LexiconCard',
  props: { card: { type: Object as PropType<LexiconCard>, required: true } },
  emits: ['open-strongs'],
  setup(props) {
    const scriptClass = computed(() => LANGUAGES[props.card.language]?.font ?? '')
    // Strong's cross-references name another entry (`H6664`); BDB's (`a.ae.aa`) have no lexeme to open.
    const isStrongs = (entry: string) => /^H\d+$/.test(entry)
    return { scriptClass, isStrongs, verseRef }
  },
})
</script>

<style lang="scss" scoped>
@import '@/_colors';

.lexicon-card {
  border: 1px solid $border;
  border-radius: 4px;
  background: $surface;
  margin-bottom: 0.9rem;
  font-size: 0.9rem;
}

header {
  display: flex;
  justify-content: space-between;
  padding: 0.3rem 0.75rem;
  background: $surface-raised;
  border-bottom: 1px solid $border;
  font-size: 0.75rem;
  letter-spacing: 0.03em;

  .title {
    color: $text-dark;
    text-transform: uppercase;
  }

  .entry {
    color: $text-light;
  }
}

.head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem 0.25rem;

  .headword {
    font-size: 1.4rem;
    color: $text-dark;
  }

  .translit,
  .pos {
    color: $text-light;
    font-size: 0.8rem;
  }

  .gloss {
    color: $text-medium;
  }
}

.body {
  padding: 0 0.75rem 0.5rem;
  line-height: 1.5;

  .paragraph {
    margin-bottom: 0.2rem;
  }

  .label {
    margin-right: 0.35rem;
    color: $text-medium;
  }

  .definition {
    color: $text-dark;
  }

  .font-hebrew,
  .font-greek {
    font-size: 1.05em;
  }

  .reference {
    color: $text-medium;
    border-bottom: 1px dotted $border-strong;
  }

  .cross {
    cursor: pointer;
  }
}

.attribution {
  padding: 0.3rem 0.75rem;
  border-top: 1px solid $border;
  color: $text-light;
  font-size: 0.7rem;
}
</style>
